export interface ChainActionDef {
  key: string;
  label: string;
  icon: string;
  danger?: boolean;
  targetStatus: string;
  requiresComment?: boolean;
  visibleStatuses: string[];
  applicationTypes?: string[];
  excludeApplicationTypes?: string[];
}

export type ChainRoleActionsMap<T extends ChainActionDef = ChainActionDef> =
  Partial<Record<string, T[]>>;

export interface ApprovalStep {
  role: string;
  action: string;
  from: string[];
  to: string;
  icon?: string;
}

export interface ApprovalArchive {
  role: string;
  from: string[];
}

export interface ApprovalChain {
  id: string;
  label: string;
  applicationTypes: string[];
  steps: ApprovalStep[];
  archive?: ApprovalArchive;
}

const slug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export function buildApprovalChainActions(
  chain: ApprovalChain,
): ChainRoleActionsMap {
  const actions: Record<string, ChainActionDef[]> = {};
  const add = (role: string, def: ChainActionDef) => {
    (actions[role] ??= []).push(def);
  };

  chain.steps.forEach((step, index) => {
    const prefix = `${chain.id}-${index + 1}-${slug(step.role)}`;

    add(step.role, {
      key: `${prefix}-${slug(step.action)}`,
      label: step.action,
      icon: step.icon ?? 'check-circle',
      targetStatus: step.to,
      visibleStatuses: step.from,
      applicationTypes: chain.applicationTypes,
    });

    add(step.role, {
      key: `${prefix}-query`,
      label: 'Raise a Query',
      icon: 'question-circle',
      danger: true,
      targetStatus: 'QUERIED',
      requiresComment: true,
      visibleStatuses: step.from,
      applicationTypes: chain.applicationTypes,
    });
  });

  if (chain.archive) {
    add(chain.archive.role, {
      key: `${chain.id}-archive`,
      label: 'Archive',
      icon: 'inbox',
      danger: true,
      targetStatus: 'ARCHIVED',
      visibleStatuses: chain.archive.from,
      applicationTypes: chain.applicationTypes,
    });
  }

  return actions;
}

export function mergeRoleActions<T extends ChainActionDef>(
  ...maps: ChainRoleActionsMap<T>[]
): ChainRoleActionsMap<T> {
  const merged: Record<string, T[]> = {};

  for (const map of maps) {
    for (const [role, defs] of Object.entries(map)) {
      merged[role] = [...(merged[role] ?? []), ...(defs ?? [])];
    }
  }

  return merged;
}

export function excludeApplicationTypes<T extends ChainActionDef>(
  map: ChainRoleActionsMap<T>,
  applicationTypes: string[],
): ChainRoleActionsMap<T> {
  const result: Record<string, T[]> = {};

  for (const [role, defs] of Object.entries(map)) {
    result[role] = (defs ?? []).map((def) => ({
      ...def,
      excludeApplicationTypes: Array.from(
        new Set([...(def.excludeApplicationTypes ?? []), ...applicationTypes]),
      ),
    }));
  }

  return result;
}

export function buildApprovalChainsActions(
  chains: ApprovalChain[],
): ChainRoleActionsMap {
  return mergeRoleActions(
    ...chains.map((chain) => buildApprovalChainActions(chain)),
  );
}

export function chainApplicationTypes(chains: ApprovalChain[]): string[] {
  return chains.flatMap((chain) => chain.applicationTypes);
}

export function selectRoles<T extends ChainActionDef>(
  map: ChainRoleActionsMap<T>,
  options: { include?: string[]; exclude?: string[] },
): ChainRoleActionsMap<T> {
  const result: Record<string, T[]> = {};

  for (const [role, defs] of Object.entries(map)) {
    if (options.include && !options.include.includes(role)) {
      continue;
    }

    if (options.exclude?.includes(role)) {
      continue;
    }

    result[role] = defs ?? [];
  }

  return result;
}

export function excludeApplicationTypesForKeys<T extends ChainActionDef>(
  map: ChainRoleActionsMap<T>,
  keys: string[],
  applicationTypes: string[],
): ChainRoleActionsMap<T> {
  const result: Record<string, T[]> = {};

  for (const [role, defs] of Object.entries(map)) {
    result[role] = (defs ?? []).map((def) =>
      keys.includes(def.key)
        ? {
            ...def,
            excludeApplicationTypes: Array.from(
              new Set([
                ...(def.excludeApplicationTypes ?? []),
                ...applicationTypes,
              ]),
            ),
          }
        : def,
    );
  }

  return result;
}

export interface ApprovalFilterValues {
  applicationTypes: string[];
  statuses: string[];
}

export function relevantApprovalFilters(
  chains: ApprovalChain[],
  roles: string[],
  applicationType?: string | null,
): ApprovalFilterValues | null {
  const applicationTypes = new Set<string>();
  const statuses = new Set<string>();

  for (const chain of chains) {
    const steps = chain.steps.filter((step) => roles.includes(step.role));
    const archive =
      chain.archive && roles.includes(chain.archive.role)
        ? chain.archive
        : undefined;

    if (!steps.length && !archive) {
      continue;
    }

    chain.applicationTypes.forEach((type) => applicationTypes.add(type));

    if (
      applicationType &&
      !chain.applicationTypes.some(
        (type) =>
          type === applicationType || type === `${applicationType}_BOND`,
      )
    ) {
      continue;
    }

    for (const step of steps) {
      step.from.forEach((status) => statuses.add(status));
      statuses.add(step.to);
      statuses.add('QUERIED');
    }

    if (archive) {
      archive.from.forEach((status) => statuses.add(status));
      statuses.add('ARCHIVED');
    }
  }

  return applicationTypes.size
    ? { applicationTypes: [...applicationTypes], statuses: [...statuses] }
    : null;
}

/**
 * Same shape as `relevantApprovalFilters`, but not narrowed to a role: every
 * status the chain(s) of the chosen application type pass through. Used for
 * roles that sit outside the chains (e.g. ADMIN), who can see every application.
 */
export function chainApprovalFilters(
  chains: ApprovalChain[],
  applicationType?: string | null,
): ApprovalFilterValues | null {
  const applicationTypes = new Set<string>();
  const statuses = new Set<string>();

  for (const chain of chains) {
    chain.applicationTypes.forEach((type) => applicationTypes.add(type));

    if (
      applicationType &&
      !chain.applicationTypes.some(
        (type) =>
          type === applicationType || type === `${applicationType}_BOND`,
      )
    ) {
      continue;
    }

    for (const step of chain.steps) {
      step.from.forEach((status) => statuses.add(status));
      statuses.add(step.to);
    }

    statuses.add('QUERIED');

    if (chain.archive) {
      chain.archive.from.forEach((status) => statuses.add(status));
      statuses.add('ARCHIVED');
    }
  }

  return applicationTypes.size
    ? { applicationTypes: [...applicationTypes], statuses: [...statuses] }
    : null;
}

export function findApprovalChain(
  chains: ApprovalChain[],
  applicationType: string,
): ApprovalChain | undefined {
  return chains.find((chain) =>
    chain.applicationTypes.includes(applicationType),
  );
}

export function isAssignedApprovalRole(
  chains: ApprovalChain[],
  role: string,
): boolean {
  return chains.some((chain) =>
    chain.steps.some((step, index) => index > 0 && step.role === role),
  );
}

export function describeApprovalChain(chain: ApprovalChain): string[] {
  return chain.steps.map(
    (step, index) =>
      `${index + 1}. ${step.role} | ${step.action} | ${step.from.join(' / ')} -> ${step.to}`,
  );
}
